import apiClient from "@/api/client";
import type {
  OfficialFeeHistoryEntry,
  OfficialFeeMonthlyTotal,
  OfficialFeeRequestDetail,
  OfficialFeeRequestFormData,
  OfficialFeeRequestList,
  SimilarOfficialFee,
} from "../types";

export interface OfficeSuggestion {
  office_name: string;
  office_account_number: string;
  office_address: string;
}

/** Project pickable on the fee form — own coordinated ones, or all of them with
 *  the "Opłaty: wszystkie przedsięwzięcia" permission. */
export interface FeeProject {
  id: string;
  name: string;
  short_name: string;
  is_active: boolean;
  company: string | null;
  company_name: string | null;
}

export interface SimilarFeesQuery {
  office_name: string;
  office_account_number: string;
  amount: string;
  paying_company: string;
}

/**
 * Statuses a monthly total counts. Rejected and withdrawn fees never left the
 * account, so summing them would inflate every month they happen to fall in.
 * Wstrzymana (`on_hold`) też nie wchodzi: autor sam zatrzymał ją przed decyzją, więc
 * na razie nikt o te pieniądze nie prosi — wróci do sumy razem z przywróceniem.
 */
const COUNTED_IN_TOTALS = [
  "pending_approval",
  "approved",
  "payment_uploaded",
  "acknowledged",
];

/**
 * Statusy, po których liczy się sumy przy WŁĄCZONYM filtrze statusów na liście:
 * przecięcie wyboru użytkownika z tym, co w ogóle wchodzi do sum. Bez przecięcia
 * zaznaczenie „Odrzucone" dopisywałoby do sumy pieniądze, które nigdy nie wyszły z
 * konta. Pusty wynik (wybrano wyłącznie statusy spoza sum) znaczy „nie ma czego
 * liczyć" — wtedy zapytania w ogóle nie wysyłamy, zamiast liczyć po wszystkim.
 */
export function countedTotalStatuses(selected?: string[]): string[] {
  if (!selected?.length) return COUNTED_IN_TOTALS;
  return selected.filter((s) => COUNTED_IN_TOTALS.includes(s));
}

const BASE = "/official-fee-requests";

export const officialFeesApi = {
  list: (params?: Record<string, string>) =>
    apiClient
      .get<{ data: OfficialFeeRequestList[]; meta: { count: number } }>(`${BASE}/`, { params })
      .then((r) => r.data),

  get: (id: string) =>
    apiClient
      .get<{ data: OfficialFeeRequestDetail }>(`${BASE}/${id}/`)
      .then((r) => r.data.data),

  create: (data: OfficialFeeRequestFormData) =>
    apiClient
      .post<{ data: OfficialFeeRequestDetail }>(`${BASE}/`, data)
      .then((r) => r.data.data),

  update: (id: string, data: Partial<OfficialFeeRequestFormData>) =>
    apiClient
      .patch<{ data: OfficialFeeRequestDetail }>(`${BASE}/${id}/`, data)
      .then((r) => r.data.data),

  delete: (id: string) => apiClient.delete(`${BASE}/${id}/`),

  addFee: (data: OfficialFeeRequestFormData, paymentFiles: File[], attachmentFiles: File[]) => {
    const fd = new FormData();
    (Object.entries(data) as [string, string | undefined][]).forEach(([k, v]) => {
      if (v !== undefined && v !== "") fd.append(k, v);
    });
    paymentFiles.forEach((f) => fd.append("payment_files", f, f.name));
    attachmentFiles.forEach((f) => fd.append("attachment_files", f, f.name));
    return apiClient
      .post<{ data: OfficialFeeRequestDetail }>(`${BASE}/add-fee/`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data.data);
  },

  approve: (id: string) =>
    apiClient
      .post<{ data: OfficialFeeRequestDetail }>(`${BASE}/${id}/approve/`)
      .then((r) => r.data.data),

  reject: (id: string, rejection_comment: string) =>
    apiClient
      .post<{ data: OfficialFeeRequestDetail }>(`${BASE}/${id}/reject/`, { rejection_comment })
      .then((r) => r.data.data),

  uploadPayment: (id: string, files: File[]) => {
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f, f.name));
    return apiClient
      .post<{ data: OfficialFeeRequestDetail }>(`${BASE}/${id}/upload-payment/`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data.data);
  },

  uploadAttachments: (id: string, files: File[]) => {
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f, f.name));
    return apiClient
      .post<{ data: OfficialFeeRequestDetail }>(`${BASE}/${id}/attachments/`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data.data);
  },

  deleteAttachment: (id: string, fileId: string) =>
    apiClient.delete(`${BASE}/${id}/attachments/${fileId}/`),

  acknowledge: (id: string) =>
    apiClient
      .post<{ data: OfficialFeeRequestDetail }>(`${BASE}/${id}/acknowledge/`)
      .then((r) => r.data.data),

  withdraw: (id: string) =>
    apiClient
      .post<{ data: OfficialFeeRequestDetail }>(`${BASE}/${id}/withdraw/`)
      .then((r) => r.data.data),

  /** Wstrzymanie własnej prośby przed akceptacją — od tej chwili widzi ją tylko autor. */
  hold: (id: string) =>
    apiClient
      .post<{ data: OfficialFeeRequestDetail }>(`${BASE}/${id}/hold/`)
      .then((r) => r.data.data),

  /** Przywrócenie wstrzymanej prośby — wraca do kolejki akceptacji. */
  restore: (id: string) =>
    apiClient
      .post<{ data: OfficialFeeRequestDetail }>(`${BASE}/${id}/restore/`)
      .then((r) => r.data.data),

  history: (id: string) =>
    apiClient
      .get<{ data: OfficialFeeHistoryEntry[] }>(`${BASE}/${id}/history/`)
      .then((r) => r.data.data),

  clone: (id: string) =>
    apiClient
      .get<{ data: OfficialFeeRequestFormData }>(`${BASE}/${id}/clone/`)
      .then((r) => r.data.data),

  pendingAcknowledgementCount: () =>
    apiClient
      .get<{ data: { count: number } }>(`${BASE}/pending_acknowledgement_count/`)
      .then((r) => r.data.data.count),

  pendingApprovalCount: () =>
    apiClient
      .get<{ data: { count: number } }>(`${BASE}/pending_approval_count/`)
      .then((r) => r.data.data.count),

  pendingPaymentCount: () =>
    apiClient
      .get<{ data: { count: number } }>(`${BASE}/pending_payment_count/`)
      .then((r) => r.data.data.count),

  availableProjects: () =>
    apiClient
      .get<{ data: FeeProject[] }>(`${BASE}/available-projects/`)
      .then((r) => r.data.data),

  officeSuggestions: (q: string) =>
    apiClient
      .get<{ data: OfficeSuggestion[] }>(`${BASE}/office-suggestions/`, { params: { q } })
      .then((r) => r.data.data),

  /**
   * Monthly sums for the table under the list — computed in the database over the
   * SAME rows the list shows (same permission scope, same filters), so the figures
   * cannot quietly cover more than the person is allowed to see.
   *
   * Repeated-key form (?status=a&status=b) — axios' default serializer emits
   * `status[]=a`, which django-filter's getlist() does not read.
   */
  monthlyTotals: (
    filters: Record<string, string | undefined>,
    /** Statusy z filtra listy; pominięte = domyślny zestaw wchodzący do sum. */
    statuses?: string[],
  ) => {
    const params = new URLSearchParams({ group_by: "created_at:month", sum: "amount" });
    countedTotalStatuses(statuses).forEach((s) => params.append("status", s));
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });
    return apiClient
      .get<{ data: OfficialFeeMonthlyTotal[] }>(`${BASE}/summary/`, { params })
      .then((r) => r.data.data);
  },

  similar: (params: SimilarFeesQuery) =>
    apiClient
      .get<{ data: SimilarOfficialFee[] }>(`${BASE}/similar/`, { params })
      .then((r) => r.data.data),
};
